import * as React from "react";
import { ThunkDispatch } from "redux-thunk";
import { StoreState } from "../../store/storeState";
import { AnyAction } from "redux";
import { setAppTitleBar } from "../../actions/appTitleBarActions";
import { TitleBar } from "../../types/titleBar";
import { connect } from "react-redux";
import { withStyles, createStyles, Button } from "@material-ui/core";
import { WithStyles, Theme } from "@material-ui/core/styles";
import CustomerSettingsForm from "./CustomerSettingsForm";
import { getCustomerPreferences, updateCustomerPreferences } from "../../actions/customerPreferencesActions";
import { loadBranches } from "../../actions/branchActions";
import { Fragment } from "react";
import { initialState } from "../../store/initialState";
import { ContactInfoValidation } from "../../utilities/fieldValidator";
import CircularProgressBlanket from "../../../common/Components/CircularProgressBlanket";
import { withRouter, RouteComponentProps } from "react-router";
import ContentCircularProgress from "../../../common/Components/general/ContentCircularProgress";
import { CustomerPreferences, Customer } from "src/external/types/customer";
import { rootFontSize } from "src/globalCssVars";

interface CustomerSettingsPageDispatchProps {
  setAppTitle: (title: TitleBar) => void;
  getPreferences: (userId: number) => Promise<void>;
  getBranches: () => Promise<void>;
  updatePreferences: (preferences: CustomerPreferences) => Promise<void>;
}

interface CustomerSettingsPageReduxProps {
  user: Customer;
  preferences: CustomerPreferences;
  textIsDisabled: boolean;
}

interface CustomerSettingsPageProps
  extends CustomerSettingsPageDispatchProps,
  CustomerSettingsPageReduxProps,
  RouteComponentProps<any>,
  WithStyles<typeof styles> { }

interface CustomerSettingsPageState {
  loading: boolean;
  preferences: CustomerPreferences;
  isValid: boolean;
  saving: boolean;
}

class CustomerSettingsPage extends React.PureComponent<CustomerSettingsPageProps, CustomerSettingsPageState> {
  private _isMounted: boolean = false;

  static getDerivedStateFromProps(nextProps: CustomerSettingsPageProps, prevState: CustomerSettingsPageState) {
    if (prevState.preferences.customerId === -1) {
      return {
        preferences: {
          ...nextProps.preferences,
          preferredEmail: getInitialEmail(nextProps),
          preferredPhone: getInitialPhone(nextProps)
        }
      };
    }
    return null;
  }

  constructor(props: CustomerSettingsPageProps) {
    super(props);
    this.state = {
      loading: false,
      preferences: { ...initialState.customerPreferences },
      isValid: true,
      saving: false
    };
  }

  async componentDidMount() {
    this._isMounted = true;
    this.props.setAppTitle({
      title: "Settings",
      description: "Update your personal settings"
    });
    try {
      if (this._isMounted) {
        this.setState({ loading: true });
      }
      await Promise.all([this.props.getPreferences(this.props.user.id), this.props.getBranches()]);
    } finally {
      if (this._isMounted) {
        this.setState({ loading: false });
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  preferenceAndValidationUpdated = (preferences: CustomerPreferences, isValid: boolean) => {
    this.setState({ preferences, isValid });
  };

  preferenceUpdated = (preferences: CustomerPreferences) => {
    this.setState({ preferences });
  };

  userUpdated = (user: Customer, valid: ContactInfoValidation) => {
    this.setState({
      isValid: valid.validEmail && valid.validPhone
    });
  };

  save = async () => {
    if (this._isMounted) {
      this.setState({ saving: true });
    }
    await this.props.updatePreferences(this.state.preferences);
    if (this._isMounted) {
      this.setState({ saving: false });
    }
    this.props.history.push("/");
  };

  cancel = () => {
    this.setState({
      preferences: {
        ...this.props.preferences,
        preferredEmail: getInitialEmail(this.props),
        preferredPhone: getInitialPhone(this.props)
      }
    });

    this.props.history.push("/");
  };

  render() {
    const { classes, user, textIsDisabled } = this.props;
    const { loading, preferences, isValid, saving } = this.state;
    return (
      <div className={classes.root}>
        {saving && <CircularProgressBlanket />}
        {loading ? (
          <ContentCircularProgress />
        ) : (
            <Fragment>
              <div className={classes.formContainer}>
                <CustomerSettingsForm
                  user={user}
                  preferences={preferences}
                  onPreferenceUpdated={this.preferenceUpdated}
                  onPreferenceAndValidationUpdated={this.preferenceAndValidationUpdated}
                  textIsDisabled={textIsDisabled}
                />
              </div>
              <div className={classes.buttons}>
                <Button
                  onClick={this.save}
                  disabled={!isValid}
                  className={classes.saveButton}
                  variant="contained"
                  color="primary"
                >
                  Save
              </Button>
                <Button onClick={this.cancel} variant="outlined" color="primary">
                  Cancel
              </Button>
              </div>
            </Fragment>
          )}
      </div>
    );
  }
}

const styles = (theme: Theme) => {
  return createStyles({
    root: {
      margin: "0 auto",
      maxWidth: `${600 / rootFontSize}rem`
    },
    formContainer: {
      padding: theme.spacing.unit * 2 + "rem",
      paddingBottom: 0,
      height: "100%",
      overflow: "auto"
    },
    buttons: {
      position: "fixed",
      bottom: 0,
      paddingBottom: theme.spacing.unit * 2 + "rem",
      paddingLeft: theme.spacing.unit * 2 + "rem",
      width: "100vw",
      background: theme.palette.background.default
    },
    saveButton: {
      marginRight: `${theme.spacing.unit}rem`
    }
  });
};

const getInitialEmail = (props: CustomerSettingsPageProps): string => {
  return props.preferences.preferredEmail ? props.preferences.preferredEmail : props.user.email ? props.user.email : "";
};

const getInitialPhone = (props: CustomerSettingsPageProps): string => {
  return props.preferences.preferredPhone ? props.preferences.preferredPhone : props.user.phone ? props.user.phone : "";
};

const mapStateToProps = (state: StoreState): CustomerSettingsPageReduxProps => {
  return {
    user: state.authentication.customer,
    preferences: state.customerPreferences,
    textIsDisabled: state.appSettings.organizationSettings.textIsDisabled
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<StoreState, void, AnyAction>
): CustomerSettingsPageDispatchProps => {
  return {
    setAppTitle: (title: any) => {
      dispatch(setAppTitleBar(title));
    },
    getPreferences: async userId => {
      await dispatch(await getCustomerPreferences(userId));
    },
    getBranches: async () => {
      await dispatch(await loadBranches(undefined, false));
    },
    updatePreferences: async (preferences: CustomerPreferences) =>
      await dispatch(updateCustomerPreferences(preferences))
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(withStyles(styles)(CustomerSettingsPage)));
